import { useEffect } from 'react';
import { needsChoice } from '~/lib/stores/window-rules';
import { useWindowStore } from '~/lib/stores/window-store';

/**
 * FRONT-3146: a third window is waiting and only two fit side by side. The
 * choice is made by clicking a column (eddy, 2026-10-26: "let me click the
 * column") — each visible window is covered by a `ReplaceTarget`
 * (window-layer.tsx). This component is only the hint above the layer and the
 * two ways back out: its Cancel button and Escape. Cancelling does NOT open
 * the new window (a restore stays minimized in the list).
 *
 * While the pick is open, Escape cancels it and never minimizes a window
 * (`shouldMinimizeOnEscape` returns false while a choice is pending).
 *
 * Selectors return a boolean / a string — never a derived array, which would
 * re-render forever under zustand v5 (window-selectors.test.ts).
 */
export function WindowChooser() {
  const open = useWindowStore((s) => needsChoice(s));

  useEffect(() => {
    if (!open) return;
    // Keyboard path: start on the first column's target.
    document.querySelector<HTMLButtonElement>('[data-testid="window-tab-replace"], [data-testid="window-replace-target"]')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      useWindowStore.getState().cancelPending();
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [open]);

  // No bar of its own (eddy, 2026-10-26: "not have a second bar, let me choose
  // one of the open tabs directly"): the shown window tabs / question chips and
  // the columns themselves are the choices. This only owns Escape and focus.
  return null;
}
